// pages/driver-management/reportV2/DriverScoreCalculatePage.jsx
'use client';

import { useMemo, useState, useEffect, useCallback } from 'react';
import DataTable from '../../../components/DataTable';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  OutlinedInput,
  InputAdornment,
  Menu,
  Paper,
  Typography,
  LinearProgress,
  Divider,
  Tooltip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import {
  getExportHeaderInfo,
  exportDriverScoreCSV,
  exportDriverScoreExcelSummary,
  exportDriverScorePDF,
  exportDriverScoreExcelDetail
} from '../../../helpers/driverScoreExport';

import PickerField from '../../../components/PickerField';
import PickerFieldTable from '../../../components/PickerFieldTable';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { useCompanyDataList } from '../../../stores/companyStore';
import { useDriverAssignList } from '../../../stores/driverAssignStore';
import { fetchDriverScoreCalculate } from '../../../stores/driverScoreCalculate';
import { useNotifier } from '../../../components/Notifier';
import DetailDriverScoreDialog from './DetailDriverScoreDialog';
import {
  fetchHowenToken,
  fetchVehicleHowenAlarms,
  fetchHowenTotalRows,
  countViolationEventsByKey,
  mapHowenAlarmsToViolationEvents,
  enrichRowWithHowenEvents,
  getActiveHowenAlarmTypes
} from '../../../helpers/howenScoreHelper';
import { summarizeDriverScoreRows } from '../../../helpers/driverScoreSummary';
import { useLanguage } from '../../../utils/i18n';

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

const toArray = (val) => (Array.isArray(val) ? val : val == null || val === '' ? [] : [val]);

export default function DriverScoreCalculatePage() {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Detail Dialog State
  const [selectedDetailData, setSelectedDetailData] = useState(null);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');

  // Export states
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Pagination Model for DataTable
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Company Filter
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanies.length === 0 || selectedCompanies.includes('all')) {
      return '';
    }
    return selectedCompanies.join(',');
  }, [selectedCompanies]);

  // Driver Filter
  const [selectedDrivers, setSelectedDrivers] = useState([]);

  const driverPickerParams = usePickerPagination({
    fetchHook: useDriverAssignList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, selectedCompaniesParam],
    searchKey: 'driver_name'
  });

  const driverColumns = [
    {
      field: 'driver_code',
      headerName: t('driverScore.driverCode', 'Driver Code'),
      flex: 1,
      renderCell: (opt) => opt?.raw?.join?.driver_driver_code || opt?.driver_code || '-'
    },
    {
      field: 'driver_name',
      headerName: t('driverScore.driver', 'Driver'),
      flex: 1,
      renderCell: (opt) => opt?.raw?.join?.driver_name || opt?.raw?.driver_name || '-'
    },
    {
      field: 'company_name',
      headerName: t('general.company', 'Company'),
      flex: 1,
      renderCell: (opt) => opt?.raw?.join?.company_name || opt?.raw?.company_name || '-'
    }
  ];

  // Date Filter (matching ReportFatiguePage)
  const [rangeOption, setRangeOption] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (!rangeOption) return;
    if (rangeOption === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (rangeOption === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (rangeOption === 'today') {
      from = now.startOf('day');
    } else if (rangeOption === 'week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (rangeOption === 'month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeOption]);

  const handleView = useCallback((rowData) => {
    setSelectedDetailData(rowData);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'start_date',
        headerName: t('driverScore.periodStart', 'Period Start'),
        flex: 1,
        valueGetter: (val, row) => (row.start_date ? dayjs(row.start_date).format('DD MMM YYYY HH:mm') : '-')
      },
      {
        field: 'end_date',
        headerName: t('driverScore.periodEnd', 'Period End'),
        flex: 1,
        valueGetter: (val, row) => (row.end_date ? dayjs(row.end_date).format('DD MMM YYYY HH:mm') : '-')
      },
      { field: 'driver_name', headerName: t('driverScore.driver', 'Driver'), flex: 1, valueGetter: (val, row) => row.driver_name || '-' },
      {
        field: 'total_score_retase',
        headerName: t('driverScore.totalScoreRetase', 'Total Score (Retase)'),
        flex: 0.8,
        valueGetter: (val, row) => (row.total_score_retase != null ? row.total_score_retase : '-')
      },
      {
        field: 'idx_name_retase',
        headerName: t('driverScore.indexRetase', 'Index (Retase)'),
        flex: 0.6,
        valueGetter: (val, row) => row.idx_name_retase || '-'
      },
      {
        field: 'total_score_non_retase',
        headerName: t('driverScore.totalScoreNonRetase', 'Total Score (Non-Retase)'),
        flex: 0.8,
        valueGetter: (val, row) => (row.total_score_non_retase != null ? row.total_score_non_retase : '-')
      },
      {
        field: 'idx_name_non_retase',
        headerName: t('driverScore.indexNonRetase', 'Index (Non-Retase)'),
        flex: 0.6,
        valueGetter: (val, row) => row.idx_name_non_retase || '-'
      },
      {
        field: 'total_score',
        headerName: t('driverScore.totalScoreCombine', 'Total Score (Combine)'),
        flex: 0.8,
        valueGetter: (val, row) => (row.total_score != null ? row.total_score : '-')
      },
      {
        field: 'idx_name',
        headerName: t('driverScore.indexCombine', 'Index (Combine)'),
        flex: 0.6,
        valueGetter: (val, row) => row.idx_name || '-'
      },
      {
        field: 'total_distance_km',
        headerName: t('driverScore.totalMileage', 'Total Mileage (KM)'),
        flex: 0.8,
        valueGetter: (val, row) => (row.total_distance_km != null ? row.total_distance_km : '-')
      },
      {
        field: 'total_trip_duration',
        headerName: t('driverScore.totalTravelTime', 'Total Travel Time'),
        flex: 1,
        valueGetter: (val, row) => row.trip_duration || row.total_trip_duration || '-'
      },
      {
        field: 'total_stop_duration',
        headerName: t('driverScore.totalStopTime', 'Total Stop Time'),
        flex: 1,
        valueGetter: (val, row) => row.stop_duration || row.total_stop_duration || '-'
      },
      {
        field: 'actions',
        headerName: t('general.action', 'Action'),
        sortable: false,
        width: 80,
        renderCell: (params) => (
          <IconButton size="small" onClick={() => handleView(params.row)} aria-label="view">
            <VisibilityIcon />
          </IconButton>
        )
      }
    ],
    [handleView, t]
  );

  // Local filtering
  useEffect(() => {
    let filtered = [...rows];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((r) => {
        if (searchColumn === 'all') {
          return Object.values(r).some((val) => String(val).toLowerCase().includes(q));
        } else {
          return String(r[searchColumn] || '')
            .toLowerCase()
            .includes(q);
        }
      });
    }

    setFilteredRows(filtered);
  }, [rows, searchQuery, searchColumn]);

  const handleCalculateScore = async () => {
    if (selectedDrivers.length === 0) {
      notify({ message: t('driverScore.pleaseSelectDriver', 'Please select at least one driver.'), severity: 'warning' });
      return;
    }

    if (!fromDate || !toDate) {
      notify({ message: t('driverScore.pleaseSelectDateRange', 'Please select valid date range.'), severity: 'warning' });
      return;
    }

    setLoading(true);
    setRows([]);
    setLoadingProgress(0);
    setLoadingText(t('driverScore.preparingCalculation', 'Preparing calculation...'));

    const localStart = dayjs(fromDate);
    const localEnd = dayjs(toDate);
    const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(rangeOption);

    // Chunking Logic matching ReportFatiguePage / DataReportsPage
    let apiChunks = [];
    if (isFullDayPeriod) {
      let curr = localStart.startOf('day');
      const end = localEnd.startOf('day');
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        let chunkStart = curr.startOf('day');
        let chunkEnd = curr.endOf('day');
        apiChunks.push({
          startLocal: chunkStart,
          endLocal: chunkEnd
        });
        curr = curr.add(1, 'day');
      }
    } else if (rangeOption === 'today') {
      let curr = localStart.startOf('day');
      const end = localEnd.startOf('day');
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        let chunkStart = curr.startOf('day');
        let chunkEnd = curr.endOf('day');
        if (curr.isSame(dayjs(), 'day')) {
          chunkEnd = localEnd;
        }
        apiChunks.push({
          startLocal: chunkStart,
          endLocal: chunkEnd
        });
        curr = curr.add(1, 'day');
      }
    } else {
      let curr = localStart.startOf('day');
      const end = localEnd.startOf('day');
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        let chunkStart = curr.startOf('day');
        let chunkEnd = curr.endOf('day');
        if (curr.isSame(localStart, 'day')) chunkStart = localStart;
        if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

        apiChunks.push({
          startLocal: chunkStart,
          endLocal: chunkEnd
        });
        curr = curr.add(1, 'day');
      }
    }

    try {
      const allResults = [];
      let totalRequests = selectedDrivers.length * apiChunks.length;
      let completedRequests = 0;

      // 1. Fetch VTDS Mapping for selected companies
      setLoadingText(t('driverScore.fetchingDeviceMapping', 'Fetching vehicle device mapping...'));

      // 2. Fetch GPS driver scores
      for (const driverId of selectedDrivers) {
        const driverObj = driverPickerParams.options.find((d) => String(d.raw?.driver) === String(driverId))?.raw;
        const compId = driverObj?.company_id || driverObj?.company;

        for (const chunk of apiChunks) {
          const payload = {
            company: Number(compId),
            driver: driverId,
            start_date: chunk.startLocal.subtract(7, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
            end_date: chunk.endLocal.subtract(7, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]')
          };

          try {
            const res = await fetchDriverScoreCalculate(payload);
            if (res) {
              if (Array.isArray(res) && res.length > 0) {
                allResults.push(...res);
              } else if (typeof res === 'object' && Object.keys(res).length > 0) {
                allResults.push(res);
              }
            }
          } catch (e) {
            console.warn('Calculate err', payload, e);
          }

          completedRequests++;
          const progress = (completedRequests / totalRequests) * 50;
          setLoadingProgress(progress);
          setLoadingText(`${t('driverScore.calculatingGps', 'Calculating GPS scores...')} ${Math.round(progress * 2)}%`);
        }
      }

      // 3. Process Howen alarms for vehicles
      setLoadingText(t('driverScore.fetchingHowen', 'Fetching Howen camera alarms...'));
      let howenToken = '';
      const hasHowenVehicles = allResults.some((r) => {
        return r.vtds?.integration_vendor == 'howen' && r.vtds?.integration_imei != '';
      });

      if (hasHowenVehicles) {
        howenToken = await fetchHowenToken();
      }

      // Cache alarms by `${deviceId}_${start_date}_${end_date}` to avoid duplicate requests
      const alarmCache = {};
      const violationEventsCache = {};
      const totalRowsCache = {};
      const enrichedResults = [];

      for (let i = 0; i < allResults.length; i++) {
        const row = allResults[i];
        const vtdsInfo = row?.vtds;
        const deviceId = vtdsInfo?.integration_imei;

        let howenCounts = {
          yawning: 0,
          smoking: 0,
          sleepy: 0,
          distraction: 0,
          phone_call: 0,
          covering_camera: 0
        };
        let howenViolationEvents = [];
        let vssTotalRows = null;

        if (deviceId && howenToken) {
          const activeTypes = getActiveHowenAlarmTypes(row.score_config_events);
          if (activeTypes.length > 0) {
            const cacheKey = `${deviceId}_${row.start_date}_${row.end_date}_${activeTypes.join(',')}`;
            if (!alarmCache[cacheKey]) {
              setLoadingText(`${t('driverScore.fetchingHowen', 'Fetching Howen camera alarms...')} (${i + 1}/${allResults.length})`);
              const rawAlarms = await fetchVehicleHowenAlarms(deviceId, row.start_date, row.end_date, howenToken, row.score_config_events);
              violationEventsCache[cacheKey] = mapHowenAlarmsToViolationEvents(rawAlarms);
              alarmCache[cacheKey] = countViolationEventsByKey(violationEventsCache[cacheKey]);
            }
            howenCounts = alarmCache[cacheKey];
            howenViolationEvents = violationEventsCache[cacheKey] || [];

            const totalRowsCacheKey = `${deviceId}_${row.start_date}_${row.end_date}`;
            if (totalRowsCache[totalRowsCacheKey] === undefined) {
              totalRowsCache[totalRowsCacheKey] = await fetchHowenTotalRows(deviceId, row.start_date, row.end_date, howenToken);
            }
            vssTotalRows = totalRowsCache[totalRowsCacheKey];
          }
        }

        const enriched = enrichRowWithHowenEvents(row, howenCounts, vssTotalRows);
        enriched._violationEvents = howenViolationEvents;
        enriched.vssTotalRows = vssTotalRows;
        enrichedResults.push(enriched);

        const howenProgress = 50 + ((i + 1) / (allResults.length || 1)) * 50;
        setLoadingProgress(howenProgress);
      }

      setLoadingText(t('driverScore.summarizing', 'Summarizing scores per driver and vehicle...'));
      const finalRows = summarizeDriverScoreRows(enrichedResults);
      setRows(finalRows);
      notify({
        message: `${t('driverScore.calcSuccess', 'Successfully calculated scores. Found records: ')}${finalRows.length}`,
        severity: 'success'
      });
    } catch (err) {
      notify({ message: err.message || t('driverScore.calcError', 'Error calculating scores'), severity: 'error' });
    } finally {
      setLoading(false);
      setLoadingText('');
      setLoadingProgress(0);
    }
  };

  // Export handlers
  const handleExportCSV = () => {
    setExportAnchorEl(null);
    if (filteredRows.length > 2000) {
      notify({
        message: t(
          'driverScore.exportLargeDataInfo',
          `Data besar (${filteredRows.length} baris), proses export mungkin memakan waktu beberapa detik.`
        ),
        severity: 'info'
      });
    }
    const headerInfo = getExportHeaderInfo(selectedCompanies, companyOptions, rangeOption, fromDate, toDate);
    try {
      exportDriverScoreCSV(filteredRows, columns, headerInfo);
    } catch (err) {
      console.error('Export CSV Error:', err);
      notify({
        message: err.message || t('driverScore.exportFailed', 'Gagal membuat file export'),
        severity: 'error'
      });
    }
  };

  const handleExportExcel = async () => {
    setExportAnchorEl(null);
    if (filteredRows.length > 2000) {
      notify({
        message: t(
          'driverScore.exportLargeDataInfo',
          `Data besar (${filteredRows.length} baris), proses export mungkin memakan waktu beberapa detik.`
        ),
        severity: 'info'
      });
    }
    const headerInfo = getExportHeaderInfo(selectedCompanies, companyOptions, rangeOption, fromDate, toDate);
    try {
      await exportDriverScoreExcelSummary(filteredRows, columns, headerInfo);
    } catch (err) {
      console.error('Export Excel Error:', err);
      notify({
        message: err.message || t('driverScore.exportFailed', 'Gagal membuat file export'),
        severity: 'error'
      });
    }
  };

  const handleExportPDF = () => {
    setExportAnchorEl(null);
    if (filteredRows.length > 2000) {
      notify({
        message: t(
          'driverScore.exportLargeDataInfo',
          `Data besar (${filteredRows.length} baris), proses export mungkin memakan waktu beberapa detik.`
        ),
        severity: 'info'
      });
    }
    const headerInfo = getExportHeaderInfo(selectedCompanies, companyOptions, rangeOption, fromDate, toDate);
    try {
      exportDriverScorePDF(filteredRows, columns, headerInfo);
    } catch (err) {
      console.error('Export PDF Error:', err);
      notify({
        message: err.message || t('driverScore.exportFailed', 'Gagal membuat file export'),
        severity: 'error'
      });
    }
  };

  const handleExportDetailExcel = async () => {
    setExportAnchorEl(null);
    if (!filteredRows || filteredRows.length === 0) return;

    const totalFiles = filteredRows.length * 2 + 1;
    if (totalFiles > 50) {
      notify({
        message: t(
          'driverScore.exportZipFilesInfo',
          `Akan membuat ${totalFiles} file dan menggabungkannya jadi 1 zip, proses mungkin memakan waktu beberapa detik.`
        ),
        severity: 'info'
      });
    }

    setIsExporting(true);
    setExportProgress(0);

    try {
      const headerInfo = getExportHeaderInfo(selectedCompanies, companyOptions, rangeOption, fromDate, toDate);
      await exportDriverScoreExcelDetail(filteredRows, headerInfo, {
        onProgress: (completed, total) => {
          setExportProgress((completed / total) * 100);
        }
      });
    } catch (err) {
      console.error('Export Detail Error:', err);
      notify({
        message: err.message || t('driverScore.exportFailed', 'Gagal membuat file export'),
        severity: 'error'
      });
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Filters Row */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" useFlexGap>
            <Box sx={{ minWidth: 200, flex: 1 }}>
              <PickerField
                label={t('general.company', 'Company')}
                multiSelect
                options={companyOptions}
                value={selectedCompanies}
                onChange={(val) => {
                  setSelectedCompanies(toArray(val));
                  setSelectedDrivers([]); // Reset drivers on company change
                }}
                searchKey="name"
                onSearch={companyPickerParams.setSearchValue}
                onLoadMore={companyPickerParams.handleLoadMore}
                isLoading={companyPickerParams.isLoading}
                disabled={loading}
              />
            </Box>

            <Box sx={{ minWidth: 250, flex: 2 }}>
              <PickerFieldTable
                label={t('driverScore.driver', 'Driver')}
                multiSelect
                options={driverPickerParams.options.map((o) => ({ ...o, value: o.raw.driver }))}
                value={selectedDrivers}
                onChange={(val) => setSelectedDrivers(toArray(val))}
                columns={driverColumns}
                onSearch={driverPickerParams.setSearchValue}
                onLoadMore={driverPickerParams.handleLoadMore}
                isLoading={driverPickerParams.isLoading}
                disabled={!selectedCompaniesParam || loading}
                placeholder={
                  !selectedCompaniesParam
                    ? t('driverScore.selectCompanyFirst', 'Select company first')
                    : t('driverScore.selectDrivers', 'Select Drivers')
                }
                getOptionLabel={(o) => {
                  const code =
                    o?.raw?.join?.driver_code ||
                    o?.raw?.join?.driver_driver_code ||
                    o?.raw?.driver_code ||
                    o?.raw?.driver_driver_code ||
                    o?.raw?.code ||
                    o?.driver_code;
                  const name = o?.raw?.join?.driver_name || o?.raw?.driver_name || o?.driver_name || o?.label || '';
                  if (code && name) {
                    return `[${code}] ${name}`;
                  }
                  return name || String(o?.value || '');
                }}
              />
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" useFlexGap>
            <FormControl
              size="small"
              sx={{
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#485D9A' },
                  '&:hover fieldset': { borderColor: '#485D9A' },
                  '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
              }}
            >
              <InputLabel id="range-select-label">{t('reportFatigue.periodRange', 'Periode Range')}</InputLabel>
              <Select
                labelId="range-select-label"
                value={rangeOption}
                label={t('reportFatigue.periodRange', 'Periode Range')}
                onChange={(e) => setRangeOption(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="today">{t('driverScore.today', 'Today')}</MenuItem>
                <MenuItem value="week">{t('reportFatigue.lastWeek', 'Last Week')}</MenuItem>
                <MenuItem value="month">{t('reportFatigue.lastMonth', 'Last Month')}</MenuItem>
                <MenuItem value="custom">{t('reportFatigue.custom', 'Custom')}</MenuItem>
              </Select>
            </FormControl>

            {rangeOption === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <DateTimePicker
                  label={t('reportFatigue.from', 'From')}
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                  timeSteps={{ minutes: 1, seconds: 1 }}
                  value={fromDate ? dayjs(fromDate) : null}
                  maxDate={toDate ? dayjs(toDate) : undefined}
                  onChange={(v) => setFromDate(v ? v.format('YYYY-MM-DD HH:mm') : '')}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        minWidth: 160,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#485D9A' },
                          '&:hover fieldset': { borderColor: '#485D9A' },
                          '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                        },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
                      }
                    }
                  }}
                />
                <DateTimePicker
                  label={t('reportFatigue.to', 'To')}
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                  timeSteps={{ minutes: 1, seconds: 1 }}
                  value={toDate ? dayjs(toDate) : null}
                  minDate={fromDate ? dayjs(fromDate) : undefined}
                  onChange={(v) => setToDate(v ? v.format('YYYY-MM-DD HH:mm') : '')}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        minWidth: 160,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#485D9A' },
                          '&:hover fieldset': { borderColor: '#485D9A' },
                          '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                        },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
                      }
                    }
                  }}
                />
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              startIcon={<AutorenewIcon />}
              onClick={handleCalculateScore}
              disabled={loading || selectedDrivers.length === 0}
            >
              {t('driverScore.searchBtn', 'Search')}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Action Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select value={searchColumn} onChange={(e) => setSearchColumn(e.target.value)} displayEmpty>
              <MenuItem value="all">{t('driverScore.allColumns', 'All Columns')}</MenuItem>
              <MenuItem value="driver_name">{t('driverScore.driver', 'Driver')}</MenuItem>
              <MenuItem value="total_score">{t('driverScore.totalScoreField', 'Total Score')}</MenuItem>
            </Select>
          </FormControl>
          <OutlinedInput
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('driverScore.searchPlaceholder', 'Search...')}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
          />
        </Stack>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
            size="small"
            disabled={isExporting}
          >
            {t('general.export', 'Export')}
          </Button>
          <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={() => setExportAnchorEl(null)}>
            <MenuItem onClick={handleExportCSV} disabled={filteredRows.length === 0}>
              {t('driverScore.exportSummaryCsv', 'Export Summary (CSV)')}
            </MenuItem>
            <MenuItem onClick={handleExportExcel} disabled={filteredRows.length === 0}>
              {t('driverScore.exportSummaryExcel', 'Export Summary (Excel)')}
            </MenuItem>
            <MenuItem onClick={handleExportPDF} disabled={filteredRows.length === 0}>
              {t('driverScore.exportSummaryPdf', 'Export Summary (PDF)')}
            </MenuItem>
            <Divider />
            <Tooltip
              title={filteredRows.length === 0 ? t('driverScore.noDataToExport', 'No data to export') : ''}
              placement="left"
              disableHoverListener={filteredRows.length > 0}
            >
              <span>
                <MenuItem onClick={handleExportDetailExcel} disabled={filteredRows.length === 0 || isExporting}>
                  {t('driverScore.exportDetailZip', 'Export Detail (ZIP per driver)')}
                </MenuItem>
              </span>
            </Tooltip>
          </Menu>
        </Box>
      </Box>

      {/* Export Detail Progress */}
      {isExporting && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('driverScore.exportingDetail', 'Exporting detail per driver...')}
            </Typography>
            {exportProgress > 0 && (
              <Typography variant="body2" color="text.secondary">
                {Math.round(exportProgress)}%
              </Typography>
            )}
          </Box>
          <LinearProgress variant="determinate" value={exportProgress} />
        </Box>
      )}

      {/* Loading Progress */}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {loadingText}
            </Typography>
            {loadingProgress > 0 && (
              <Typography variant="body2" color="text.secondary">
                {Math.round(loadingProgress)}%
              </Typography>
            )}
          </Box>
          <LinearProgress variant="determinate" value={loadingProgress} />
        </Box>
      )}

      {/* DataTable */}

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        loadingText={loadingText}
        paginationMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        hideAddButton={true}
        hideSearchField={true}
        hideExportButton={true}
      />
      <DetailDriverScoreDialog open={Boolean(selectedDetailData)} onClose={() => setSelectedDetailData(null)} data={selectedDetailData} />
    </LocalizationProvider>
  );
}
